import { ApiClient } from './api-client';
import { Observable, throwError } from 'rxjs';
import { Endpoints } from './endpoints';
import { inject, Injectable } from '@angular/core';
import { catchError } from 'rxjs/operators';
import { LoggableAPI } from '../models/protocols/loggable-api';
import { CustomError } from '../models/shared/custom-error';
import { State } from '../models/types/dto/state';
import { Gender } from '../models/types/dto/gender';
import { Country } from '../models/types/dto/country';
import { SubmissionStatus } from '../models/shared/submission-status';
import { AlertType } from '../models/alerts/dto/alert-types';
import { TransactionEntryCodeType } from '../models/transactions/transaction-entry-code-type';
import { TransactionStatus } from '../models/transactions/transaction-status';
import { RemittanceStatusType } from '../models/remittances/remittance-status-type';
import { MfaType } from '../models/types/dto/mfa-type';
import { RemittanceFrequencyType } from '../models/shared/remittance-frequency-type';
import { DesignatedUnitType } from '../models/shared/designated-unit-type';
import { DesignatedUnitContributionRateBasisType } from '../models/employers/plans/designated-unit-contribution-rate-basis-type';
import { DesignatedUnitContributionRateCalculationType } from '../models/employers/plans/designated-unit-contribution-rate-calculation-type';
import { DesignatedUnitLessThanFullTimeParticipationType } from '../models/employers/plans/designated-unit-less-than-full-time-participation-type';
import { EmploymentType } from '../models/employers/employer-history/types/employment-type';
import { LeaveDetailsType } from '../models/employers/employer-history/types/leave-details-type';
import { EmploymentStatusType } from '../models/employers/employer-history/types/employment-status-type';
import { RelationshipStatusType } from '../models/types/dto/relationship-status-type';
import { Pronoun } from '../models/users/dto/pronoun';
import { FundType } from '../models/transactions/fund-type';
import { BeneficiaryRelationshipToMemberType } from '../features/beneficiaries/models/beneficiary-relationship-to-member-type';
import { BeneficiarySpouseDefinitionType } from '../features/beneficiaries/models/beneficiary-spouse-definition-type';
import { FiduciaryType } from '../views/fiduciaries/models/fiduciary-type';
import { FiduciaryEntityType } from '../views/fiduciaries/models/fiduciary-entity-type';
import { FiduciaryPowerOfAttorneyType } from '../views/fiduciaries/models/fiduciary-power-of-attorney-type';
import { FiduciaryPowerOfAttorneyAppointmentType } from '../views/fiduciaries/models/fiduciary-power-of-attorney-appointment-type';
import { EmploymentStatusChangeDefinitionType } from '../models/employers/employer-history/types/employment-status-change-definition-type';
import { BaseType } from '../models/base/base-type';
import { GoverningJurisdictionType } from '../models/employers/employer-history/types/governing-jurisdiction-type';
import { SpousalWaiverType } from '../models/spousal-waivers/spousal-waiver-type';
import { EmployerPlanType } from '../models/employers/plans/employer-plan-type';
import { AnnuityGuaranteePeriodType } from '../models/shared/annuity-guarantee-period-type';
import { TaxFormType } from '../models/taxes/tax-form-type';
import { TaxResidencyType } from '../models/taxes/tax-residency-type';
import { TaxStatusType } from '../models/taxes/tax-status-type';
import { TaxProfileSourceType } from '../models/taxes/tax-profile-source-type';
import { PrescribedTaxZoneType } from '../models/taxes/prescribed-tax-zone-type';
import { TaxWithholdingOverrideType } from '../models/taxes/tax-withholding-override-type';
import { TaxCraLetterOfAuthorityType } from '../models/taxes/tax-cra-letter-of-authority-type';
import { RetirementType } from '../models/retirement/retirement-type';
import { LifeStatus } from '../models/types/dto/life-status';
import { PaylineCurrencyType } from '../views/banking/models/payline-currency-type';
import { PaymentMethodType } from '../models/shared/payment-method-type';
import { MemberStatusType } from '../models/users/dto/member-status-type';
import { IdentityType } from '../models/users/dto/identity-type';

@Injectable({
  providedIn: 'root'
})
export class TypeAPI implements LoggableAPI {
  private apiClient = inject(ApiClient);

  // Variables

  public serviceName = 'Types';

  // States

  public getStatesByCountry(countryId: number): Observable<State[]> {
    const url = Endpoints.getStatesByCountryId(countryId);
    return this.apiClient.getArr<State>(State, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Members

  public getGenders(): Observable<Gender[]> {
    const url = Endpoints.getGenders();
    return this.apiClient.getArr<Gender>(Gender, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getPronouns(): Observable<Pronoun[]> {
    const url = Endpoints.getPronouns();
    return this.apiClient.getArr<Pronoun>(Pronoun, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getMemberRelationshipStatusTypes(): Observable<RelationshipStatusType[]> {
    const url = Endpoints.getMemberRelationshipStatusTypes();
    return this.apiClient.getArr<RelationshipStatusType>(RelationshipStatusType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Countries

  public getCountries(): Observable<Country[]> {
    const url = Endpoints.getCountries();
    return this.apiClient.getArr<Country>(Country, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getCountriesWithStates(): Observable<Country[]> {
    const url = Endpoints.getCountriesWithStates();
    return this.apiClient.getArr<Country>(Country, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Submissions

  public getSubmissionStatusTypes(): Observable<SubmissionStatus[]> {
    const url = Endpoints.getSubmissionStatusTypes();
    return this.apiClient.getArr<SubmissionStatus>(SubmissionStatus, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Transactions

  public getTransactionStatusTypes(): Observable<TransactionStatus[]> {
    const url = Endpoints.getTransactionStatusTypes();
    return this.apiClient.getArr<TransactionStatus>(TransactionStatus, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Remittances

  public getRemittanceStatusTypes(): Observable<RemittanceStatusType[]> {
    const url = Endpoints.getRemittanceStatusTypes();
    return this.apiClient.getArr<RemittanceStatusType>(RemittanceStatusType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getRemittancePaymentStatusTypes(): Observable<RemittanceStatusType[]> {
    const url = Endpoints.getRemittancePaymentStatusTypes();
    return this.apiClient.getArr<RemittanceStatusType>(RemittanceStatusType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getRemittanceFrequencyTypes(): Observable<RemittanceFrequencyType[]> {
    const url = Endpoints.getRemittanceFrequencyTypes();
    return this.apiClient.getArr<RemittanceFrequencyType>(RemittanceFrequencyType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Entries

  public getTransactionEntryCodeTypes(): Observable<TransactionEntryCodeType[]> {
    const url = Endpoints.getTransactionEntryCodeTypes();
    return this.apiClient.getArr<TransactionEntryCodeType>(TransactionEntryCodeType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Contribution Entry Options

  public getContributionEntryCodeTypes(): Observable<TransactionEntryCodeType[]> {
    const url = Endpoints.getContributionEntryOptionTypes();
    return this.apiClient.getArr<TransactionEntryCodeType>(TransactionEntryCodeType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Alerts

  public getAlertTypes(): Observable<AlertType[]> {
    const url = Endpoints.getAlertTypes();
    return this.apiClient.getArr<AlertType>(AlertType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Multi-Factor Authentication Types

  public getMfaTypes(): Observable<MfaType[]> {
    const url = Endpoints.getMfaTypes();
    return this.apiClient.getArr<AlertType>(AlertType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Employer Plans

  public getEmployerPlanDesignatedUnitTypes(): Observable<DesignatedUnitType[]> {
    const url = Endpoints.getEmployerPlanDesignatedUnitTypes();
    return this.apiClient.getArr<DesignatedUnitType>(DesignatedUnitType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getEmployerPlanContributionRateBasisTypes(): Observable<DesignatedUnitContributionRateBasisType[]> {
    const url = Endpoints.getEmployerPlanContributionRateBasisTypes();
    return this.apiClient
      .getArr<DesignatedUnitContributionRateBasisType>(DesignatedUnitContributionRateBasisType, url)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public getEmployerPlanContributionRateCalculationTypes(): Observable<
    DesignatedUnitContributionRateCalculationType[]
  > {
    const url = Endpoints.getEmployerPlanContributionRateCalculationTypes();
    return this.apiClient
      .getArr<DesignatedUnitContributionRateCalculationType>(DesignatedUnitContributionRateCalculationType, url)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public getEmployerPlanLessThanFullTimeParticipationTypes(): Observable<
    DesignatedUnitLessThanFullTimeParticipationType[]
  > {
    const url = Endpoints.getEmployerPlanLessThanFullTimeParticipationTypes();
    return this.apiClient
      .getArr<DesignatedUnitLessThanFullTimeParticipationType>(DesignatedUnitLessThanFullTimeParticipationType, url)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  // Member Employment History

  public getMemberEmploymentTypes(): Observable<EmploymentType[]> {
    const url = Endpoints.getMemberEmploymentTypes();
    return this.apiClient.getArr<EmploymentType>(EmploymentType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getMemberEmploymentLeaveTypes(): Observable<LeaveDetailsType[]> {
    const url = Endpoints.getMemberEmploymentLeaveTypes();
    return this.apiClient.getArr<LeaveDetailsType>(LeaveDetailsType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getMemberEmploymentStatusTypes(): Observable<EmploymentStatusType[]> {
    const url = Endpoints.getMemberEmploymentStatusTypes();
    return this.apiClient.getArr<EmploymentStatusType>(EmploymentStatusType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getMemberEmploymentStatusChangeReasonTypes(): Observable<BaseType[]> {
    const url = Endpoints.getMemberEmploymentStatusChangeReasonTypes();
    return this.apiClient.getArr<BaseType>(BaseType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getMemberEmploymentStatusChangeDefinitionTypes(): Observable<EmploymentStatusChangeDefinitionType[]> {
    const url = Endpoints.getMemberEmploymentStatusChangeDefinitionTypes();
    return this.apiClient.getArr<EmploymentStatusChangeDefinitionType>(EmploymentStatusChangeDefinitionType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Fund Types
  public getFundTypes(): Observable<FundType[]> {
    const url = Endpoints.getFundTypes();
    return this.apiClient.getArr<FundType>(FundType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Currency Types
  public getPaylineCurrencyTypes(): Observable<PaylineCurrencyType[]> {
    const url = Endpoints.getPaylineCurrencyTypes();
    return this.apiClient.getArr<PaylineCurrencyType>(PaylineCurrencyType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Payment Method Types
  public getPaymentMethodTypes(): Observable<PaymentMethodType[]> {
    const url = Endpoints.getPaymentMethodTypes();
    return this.apiClient.getArr<PaymentMethodType>(PaymentMethodType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Beneficiary Relationship to Member Types
  public getBeneficiaryRelationshipToMemberTypes(): Observable<BeneficiaryRelationshipToMemberType[]> {
    const url = Endpoints.getBeneficiaryRelationshipToMemberTypes();
    return this.apiClient.getArr<BeneficiaryRelationshipToMemberType>(BeneficiaryRelationshipToMemberType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Beneficiary Spousal Definition Types
  public getBeneficiarySpousalDefinitionTypes(): Observable<BeneficiarySpouseDefinitionType[]> {
    const url = Endpoints.getBeneficiarySpousalDefinitionTypes();
    return this.apiClient.getArr<BeneficiarySpouseDefinitionType>(BeneficiarySpouseDefinitionType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Fiduciaries

  public getFiduciaryTypes(): Observable<FiduciaryType[]> {
    const url = Endpoints.getFiduciaryTypes();
    return this.apiClient.getArr<FiduciaryType>(FiduciaryType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getFiduciaryEntityTypes(): Observable<FiduciaryEntityType[]> {
    const url = Endpoints.getFiduciaryEntityTypes();
    return this.apiClient.getArr<FiduciaryEntityType>(FiduciaryEntityType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getFiduciaryPowerOfAttorneyTypes(): Observable<FiduciaryPowerOfAttorneyType[]> {
    const url = Endpoints.getFiduciaryPowerOfAttorneyTypes();
    return this.apiClient.getArr<FiduciaryPowerOfAttorneyType>(FiduciaryPowerOfAttorneyType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getFiduciaryPowerOfAttorneyAppointmentTypes(): Observable<FiduciaryPowerOfAttorneyAppointmentType[]> {
    const url = Endpoints.getFiduciaryPowerOfAttorneyAppointmentTypes();
    return this.apiClient
      .getArr<FiduciaryPowerOfAttorneyAppointmentType>(FiduciaryPowerOfAttorneyAppointmentType, url)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  // Employers

  public getEmployerTypes(): Observable<BaseType[]> {
    const url = Endpoints.getEmployerTypes();
    return this.apiClient.getArr<BaseType>(BaseType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getEmployerStatusTypes(): Observable<BaseType[]> {
    const url = Endpoints.getEmployerStatusTypes();
    return this.apiClient.getArr<BaseType>(BaseType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getMemberStatusTypes(): Observable<MemberStatusType[]> {
    const url = Endpoints.getMemberStatusTypes();
    return this.apiClient.getArr<MemberStatusType>(MemberStatusType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getIdentityTypes(): Observable<IdentityType[]> {
    const url = Endpoints.getIdentityTypes();
    return this.apiClient.getArr<IdentityType>(IdentityType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getGoverningJurisdictionTypes(): Observable<GoverningJurisdictionType[]> {
    const url = Endpoints.getGoverningJurisdictionTypes();
    return this.apiClient.getArr<GoverningJurisdictionType>(GoverningJurisdictionType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getTransferInSourceAccountTypes(): Observable<BaseType[]> {
    const url = Endpoints.getTransferInSourceAccountTypes();
    return this.apiClient.getArr<BaseType>(BaseType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getEmployerVotingRegionTypes(): Observable<BaseType[]> {
    const url = Endpoints.getEmployerVotingRegionTypes();
    return this.apiClient.getArr<BaseType>(BaseType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getEmployerPlanTypes(): Observable<EmployerPlanType[]> {
    const url = Endpoints.getEmployerPlanTypes();
    return this.apiClient.getArr<EmployerPlanType>(EmployerPlanType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getSpousalWaiverTypes(): Observable<SpousalWaiverType[]> {
    const url = Endpoints.getSpousalWaiverTypes();
    return this.apiClient.getArr<SpousalWaiverType>(SpousalWaiverType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getAnnuityGuaranteePeriodTypes(): Observable<AnnuityGuaranteePeriodType[]> {
    const url = Endpoints.getAnnuityGuaranteePeriodTypes();
    return this.apiClient.getArr(AnnuityGuaranteePeriodType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getAnnuityTypes(): Observable<BaseType[]> {
    const url = Endpoints.getAnnuityTypes();
    return this.apiClient.getArr<BaseType>(BaseType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Taxes

  public getTaxFormTypes(): Observable<TaxFormType[]> {
    const url = Endpoints.getTaxFormTypes();
    return this.apiClient.getArr<TaxFormType>(TaxFormType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getTaxResidencyTypes(): Observable<TaxResidencyType[]> {
    const url = Endpoints.getTaxResidencyTypes();
    return this.apiClient.getArr<TaxResidencyType>(TaxResidencyType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getTaxStatusTypes(): Observable<TaxStatusType[]> {
    const url = Endpoints.getTaxStatusTypes();
    return this.apiClient.getArr<TaxStatusType>(TaxStatusType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getTaxProfileSourceTypes(): Observable<TaxProfileSourceType[]> {
    const url = Endpoints.getTaxProfileSourceTypes();
    return this.apiClient.getArr<TaxProfileSourceType>(TaxProfileSourceType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getPrescribedTaxZoneTypes(): Observable<PrescribedTaxZoneType[]> {
    const url = Endpoints.getPrescribedTaxZoneTypes();
    return this.apiClient.getArr<PrescribedTaxZoneType>(PrescribedTaxZoneType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getTaxWithholdingOverrideTypes(): Observable<TaxWithholdingOverrideType[]> {
    const url = Endpoints.getTaxWithholdingOverrideTypes();
    return this.apiClient.getArr<TaxWithholdingOverrideType>(TaxWithholdingOverrideType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getTaxCraLetterOfAuthorityTypes(): Observable<TaxCraLetterOfAuthorityType[]> {
    const url = Endpoints.getTaxCraLetterOfAuthorityTypes();
    return this.apiClient.getArr<TaxCraLetterOfAuthorityType>(TaxCraLetterOfAuthorityType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getRetirementTypes(): Observable<RetirementType[]> {
    const url = Endpoints.getRetirementTypes();
    return this.apiClient.getArr<RetirementType>(RetirementType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getLifeStatusTypes(): Observable<LifeStatus[]> {
    const url = Endpoints.getLifeStatusTypes();
    return this.apiClient.getArr<LifeStatus>(LifeStatus, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getDeathRecordContactMethodTypes(): Observable<BaseType[]> {
    const url = Endpoints.getDeathRecordContactMethodTypes();
    return this.apiClient.getArr<BaseType>(BaseType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getDeathRecordSettlementTypes(): Observable<BaseType[]> {
    const url = Endpoints.getDeathRecordSettlementTypes();
    return this.apiClient.getArr<BaseType>(BaseType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }
}
